import { Button } from '@/components/ui/button';
import LayoutInside from '@/layout/LayoutInside';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAffiliateReport } from '@/hooks/useAffiliateReport';
import AffiliateDetailsSheet from './partials/AffiliateDetailsSheet';
import AffiliateProductPickerDialog from './partials/AffiliateProductPickerDialog';
import AffiliateRankingsSection from './partials/AffiliateRankingsSection';
import AffiliateReportTable from './partials/AffiliateReportTable';
import AffiliateReportToolbar from './partials/AffiliateReportToolbar';
import AffiliateSummaryCards from './partials/AffiliateSummaryCards';
import { AffiliateReportRow } from '@/@types/AffiliateReport';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: 'easeOut' },
  },
};

export default function GlobalAffiliateReport() {
  const { t } = useTranslation(['reports', 'common']);
  const navigate = useNavigate();

  const rankingLabels = useMemo(
    () => ({
      topRevenue: t('reports:affiliateReport.rankings.top_revenue'),
      topAov: t('reports:affiliateReport.rankings.top_aov'),
      topProfit: t('reports:affiliateReport.rankings.top_profit'),
      topRefund: t('reports:affiliateReport.rankings.top_refund'),
      topChargeback: t('reports:affiliateReport.rankings.top_chargeback'),
      empty: t('reports:affiliateReport.rankings.empty'),
    }),
    [t],
  );

  const {
    summary,
    rankings,
    tableData,
    isLoadingSummary,
    isLoadingRankings,
    isLoadingTable,
    currentPage,
    setCurrentPage,
    search,
    setSearch,
    sortBy,
    setSortBy,
    dateRange,
    handleDateRangeChange,
    products,
    setProducts,
    affiliates,
    setAffiliates,
    offers,
    setOffers,
    checkouts,
    setCheckouts,
    templates,
    setTemplates,
    clearAllFilters,
    hasActiveFiltersBeyondDates,
  } = useAffiliateReport(rankingLabels);

  const [detailsAffiliate, setDetailsAffiliate] =
    useState<AffiliateReportRow | null>(null);
  const [productPickerAffiliate, setProductPickerAffiliate] =
    useState<AffiliateReportRow | null>(null);

  const breadcrumbItems = [
    { text: t('common:reports'), link: '/reports' },
    {
      text: t('reports:profit_per_affiliate'),
      link: '/reports/profit_per_affiliate',
    },
  ];

  const handleApplyFilters = (data: {
    products?: { value: string; label: string }[];
    affiliates?: { value: string; label: string }[];
    offers?: { value: string; label: string }[];
    checkouts?: { value: string; label: string }[];
    templates?: { value: string; label: string }[];
    sortBy?: typeof sortBy;
  }) => {
    if (data.products !== undefined) setProducts(data.products);
    if (data.affiliates !== undefined) setAffiliates(data.affiliates);
    if (data.offers !== undefined) setOffers(data.offers);
    if (data.checkouts !== undefined) setCheckouts(data.checkouts);
    if (data.templates !== undefined) setTemplates(data.templates);
    if (data.sortBy !== undefined) setSortBy(data.sortBy);
  };

  const handleEditConfirm = (productId: string) => {
    navigate(`/products/edit-product/${productId}/affiliate`);
  };

  return (
    <LayoutInside hasBgBlur items={breadcrumbItems}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid gap-6"
      >
        <Link to="/reports" className="w-fit">
          <Button
            variant="default"
            className="bg-transparent text-colorText hover:bg-transparent p-0"
          >
            <ArrowLeft size={16} />
            {t('common:back')}
          </Button>
        </Link>

        <div>
          <h1 className="text-lg font-semibold">
            {t('reports:affiliateReport.title')}
          </h1>
          <p className="text-xs text-zinc-500">
            {t('reports:affiliateReport.subtitle')}
          </p>
        </div>

        <motion.div variants={itemVariants}>
          <AffiliateReportToolbar
            search={search}
            onSearchChange={setSearch}
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            sortBy={sortBy}
            products={products}
            affiliates={affiliates}
            offers={offers}
            checkouts={checkouts}
            templates={templates}
            onApplyFilters={handleApplyFilters}
            onRemoveProduct={(value) =>
              setProducts(products.filter((p) => p.value !== value))
            }
            onRemoveAffiliate={(value) =>
              setAffiliates(affiliates.filter((a) => a.value !== value))
            }
            onRemoveOffer={(value) =>
              setOffers(offers.filter((o) => o.value !== value))
            }
            onRemoveCheckout={(value) =>
              setCheckouts(checkouts.filter((c) => c.value !== value))
            }
            onRemoveTemplate={(value) =>
              setTemplates(templates.filter((c) => c.value !== value))
            }
            onRemoveSort={() => setSortBy('revenue')}
            clearAllFilters={clearAllFilters}
            hasActiveFiltersBeyondDates={hasActiveFiltersBeyondDates}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <AffiliateSummaryCards
            summary={summary}
            isLoading={isLoadingSummary}
            revenueLabel={t('reports:affiliateReport.summary.total_revenue')}
            commissionsLabel={t(
              'reports:affiliateReport.summary.total_commissions',
            )}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <AffiliateRankingsSection
            rankings={rankings}
            isLoading={isLoadingRankings}
            labels={{
              revenue: t('reports:affiliateReport.rankings.value_revenue'),
              aov: t('reports:affiliateReport.rankings.value_aov'),
              profit: t('reports:affiliateReport.rankings.value_profit'),
            }}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <AffiliateReportTable
            tableData={tableData}
            isLoading={isLoadingTable}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            dateRange={dateRange}
            products={products}
            offers={offers}
            checkouts={checkouts}
            onOpenDetails={setDetailsAffiliate}
            onOpenProductPicker={setProductPickerAffiliate}
          />
        </motion.div>
      </motion.div>

      <AffiliateDetailsSheet
        affiliate={detailsAffiliate}
        isOpen={!!detailsAffiliate}
        onClose={() => setDetailsAffiliate(null)}
      />

      <AffiliateProductPickerDialog
        products={productPickerAffiliate?.products ?? []}
        affiliateName={productPickerAffiliate?.name ?? ''}
        isOpen={!!productPickerAffiliate}
        onClose={() => setProductPickerAffiliate(null)}
        onConfirm={handleEditConfirm}
      />
    </LayoutInside>
  );
}
